import { Modal } from "../../../components/Modal";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import Select from "../../../components/Select";
import { Formik, Form } from "formik";
import { H2, H3, H4 } from "../../../components/Heading";
import { FormRow } from "../../../components/Form";
import { Paragraph } from "../../../components/Paragraph";
import { KeyboardEvent, useEffect, useState } from "react";
import { Slider } from "../../../components/Slider";
import { FaPlus, FaTrash } from "react-icons/fa";
import { GrayCard } from "../../../components/Card";
import { DateTime } from "luxon";
import { DatePicker } from "../../../components/DatePicker/index";
import { useCountries } from "../../../data/useCountries";
import { useConditionTypeList } from "../../../data/useConditionTypeList";
import { useConditionValueTypeList } from "../../../data/useConditionValueTypeList";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { queryClient } from "../../../queryClient";
import { useCreateCondition } from "../../../data/useCreateCondition";
import { useCreateBulkCondition } from "../../../data/useCreateBulkCondition";
import { useConditionsList } from "../../../data/useConditionList";
import { useUpdateCondition } from "../../../data/useUpdateCondition";
import { useDeleteCondition } from "../../../data/useDeleteCondition";
import { useCurrency } from "../../../data/useCurrency";
import { useUpdateSection } from "../../../data/useUpdateSection";

const CONDITION_DATE_FORMAT = "dd-MM-yyyy";

const formatConditionDateValue = (value: any) => {
  if (!value) return "";

  if (value instanceof Date) {
    const dateTime = DateTime.fromJSDate(value);
    return dateTime.isValid ? dateTime.toFormat(CONDITION_DATE_FORMAT) : "";
  }

  if (typeof value === "string") {
    const fromFormattedDate = DateTime.fromFormat(
      value,
      CONDITION_DATE_FORMAT,
    );
    if (fromFormattedDate.isValid) {
      return fromFormattedDate.toFormat(CONDITION_DATE_FORMAT);
    }

    const fromIsoDate = DateTime.fromISO(value);
    if (fromIsoDate.isValid) {
      return fromIsoDate.toFormat(CONDITION_DATE_FORMAT);
    }
  }

  return "";
};

const parseConditionDateValue = (value: any) => {
  if (!value) return null;
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "string") {
    const fromFormattedDate = DateTime.fromFormat(
      value,
      CONDITION_DATE_FORMAT,
    );
    if (fromFormattedDate.isValid) {
      return fromFormattedDate.toJSDate();
    }

    const fromIsoDate = DateTime.fromISO(value);
    if (fromIsoDate.isValid) {
      return fromIsoDate.toJSDate();
    }
  }

  return null;
};

const getConditionDatePickerValue = (value: any): Date | string | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const fromFormattedDate = DateTime.fromFormat(
      value,
      CONDITION_DATE_FORMAT,
    );
    if (fromFormattedDate.isValid) {
      return fromFormattedDate.toJSDate();
    }

    const fromIsoDate = DateTime.fromISO(value);
    if (fromIsoDate.isValid) {
      return fromIsoDate.toJSDate();
    }

    return value;
  }

  return null;
};

const getConditionDateKey = (condition: any, index: number) =>
  condition?.id ? `existing-${condition.id}` : `new-${index}`;

export const FormConditions = ({
  isOpen,
  onRequestClose,
  sections,
  currentIndex,
}) => {
  const { data: conditionTypeList } = useConditionTypeList();
  const { data: conditionValueTypeList } = useConditionValueTypeList();
  const { data: countryData } = useCountries();
  const { data: currencyData } = useCurrency();
  const [conditions, setConditions] = useState([]);
  const [conditionDateDrafts, setConditionDateDrafts] = useState<
    Record<string, Date | string | null>
  >({});
  const [resetConditionDateOnNextKeypress, setResetConditionDateOnNextKeypress] =
    useState<Record<string, boolean>>({});
  const [isConditionsDelete, setIsConditionsDelete] = useState(false);
  const [deleteConditionId, setDeleteConditionId] = useState(null);
  const { mutateAsync: createCondition } = useCreateCondition();
  const { mutateAsync: createBulkCondition } = useCreateBulkCondition();
  const { mutateAsync: updateFormConditions } = useUpdateCondition();
  const { mutate: deleteCondition } = useDeleteCondition();
  const sectionId = sections?.[currentIndex]?.id;
  const { data: conditionsList } = useConditionsList(sectionId ?? 0);
  const { mutate: updateSection } = useUpdateSection();

  useEffect(() => {
    if (!conditionsList) return;

    const mapped = conditionsList.results.map((c: any) => {
      return {
        id: c.id,
        section: c.section,
        question: c.question,
        _type: c._type?.id,
        value_type: c.value_type?.id,
        value: c.value ?? "",
      };
    });
    setConditions(mapped);
    setConditionDateDrafts(
      Object.fromEntries(
        mapped.map((condition: any, index: number) => [
          getConditionDateKey(condition, index),
          getConditionDatePickerValue(condition.value),
        ]),
      ),
    );
    setResetConditionDateOnNextKeypress(
      Object.fromEntries(
        mapped.map((condition: any, index: number) => [
          getConditionDateKey(condition, index),
          Boolean(condition.value),
        ]),
      ),
    );
  }, [conditionsList, sections]);

  const ConditionLabelOptions = [
    { value: "AND", label: "All the conditions must apply" },
    { value: "OR", label: "One of the conditions applies" },
  ];

  const previousQuestions = sections
    .slice(0, currentIndex)
    .flatMap((s) => s.questions);
  const previousQuestionOptions = previousQuestions?.map(
    ({ id, title, field_type }) => ({
      label: title,
      value: id,
      slug: field_type.slug_name,
    }),
  );

  const countryList = countryData?.data?.map(({ id, name, dialcode }) => ({
    label: name,
    value: id,
    dialcode: dialcode,
  }));

  const phoneNumberCountryList = countryData?.data?.map(
    ({ id, name, dialcode }) => ({
      //label: '(' + dialcode + ')' + '  ' + name,
      label: `(${dialcode}) ${name}`,
      value: id,
    }),
  );

  const currencyValue = currencyData?.data?.map(({ id, code, name }) => ({
    label: `(${code} ${name})`,
    value: id,
  }));

  const addCondition = () => {
    setConditions((prev) => {
      const nextConditions = [
        ...prev,
        {
          section: sectionId,
          question: "",
          _type: "",
          value_type: "",
          value: "",
        },
      ];
      const newIndex = nextConditions.length - 1;
      const newKey = getConditionDateKey(nextConditions[newIndex], newIndex);

      setConditionDateDrafts((previous) => ({
        ...previous,
        [newKey]: null,
      }));
      setResetConditionDateOnNextKeypress((previous) => ({
        ...previous,
        [newKey]: false,
      }));

      return nextConditions;
    });
  };

  const updateSectionOperator = (index: number, opeartor: any) => {
    const updatedSections = [...sections];
    const sectionToSave = { ...updatedSections[index] };
    if (
      sectionToSave._form &&
      typeof sectionToSave._form === "object" &&
      sectionToSave._form.id
    ) {
      sectionToSave._form = sectionToSave._form.id;
    }
    const sectionId = sectionToSave.id ? sectionToSave.id : null;
    delete sectionToSave.id;
    if (sectionToSave.questions) {
      sectionToSave.questions = sectionToSave.questions.map((question) => {
        if (question.id) {
          return {
            ...question,
            field_type: question?.field_type?.id || question?.field_type,
          };
        } else {
          return question;
        }
      });
    }
    delete sectionToSave.condition;
    sectionToSave.operator = opeartor;
    if (sectionId) {
      updateSection(
        { params: sectionToSave, sectionId: sectionId },
        {
          onSuccess: (res) => {
            notify("Section Operator details updated successfully!", {
              type: "success",
              position: "bottom-left",
            });
            queryClient.invalidateQueries(["formSections"]);
          },
          onError: (err) => {
            showError(err, "A problem occurred while creating the section.");
            updatedSections[index].isEditing = true; // Keep edit mode even on error
          },
        },
      );
    }
  };
  const updateCondition = (index: number, field: string, value: any) => {
    setConditions((prev) =>
      prev.map((con, i) => {
        if (i !== index) return con;

        const updated: any = { ...con };

        const currentQuestion = previousQuestions.find(
          (q) => q.id === updated.question,
        );
        const qSlug = currentQuestion?.field_type?.slug_name || "";

        // Currency pair handling
        if (
          qSlug === "currency" &&
          (field === "currencyCode" || field === "currencyAmount")
        ) {
          let existingCode = null;
          let existingAmount = null;

          if (
            typeof updated.value === "string" &&
            updated.value.includes("|")
          ) {
            const parts = updated.value.split("|");
            existingCode = parts[0] || null;
            existingAmount = parts[1] || null;
          }

          if (field === "currencyCode") {
            updated.currencyCode = value;
          }

          if (field === "currencyAmount") {
            updated.currencyAmount = value;
          }

          const code = updated.currencyCode ?? existingCode;
          const amount = updated.currencyAmount ?? existingAmount;

          if (code && amount) {
            updated.value = `${code}|${amount}`;
          } else {
            updated.value = "";
          }

          return updated;
        }

        // Phone pair handling
        if (
          field === "phoneNumberValue" ||
          field === "phoneNumberCountryCode"
        ) {
          let existingPhoneNumberCountryCode = null;
          let existingPhoneNumber = null;

          if (
            typeof updated.value === "string" &&
            updated.value.includes("|")
          ) {
            const parts = updated.value.split("|");
            existingPhoneNumberCountryCode = parts[0] || null;
            existingPhoneNumber = parts[1] || null;
          }

          if (field === "phoneNumberCountryCode") {
            updated.phoneNumberCountryCode = value;
          }

          if (field === "phoneNumberValue") {
            updated.phoneNumberValue = value;
          }

          const phoneNumberCountryCode =
            updated.phoneNumberCountryCode ?? existingPhoneNumberCountryCode;

          const phoneNumberValue =
            updated.phoneNumberValue ?? existingPhoneNumber;

          if (phoneNumberCountryCode && phoneNumberValue) {
            updated.value = `${phoneNumberCountryCode}|${phoneNumberValue}`;
          } else {
            updated.value = "";
          }

          return updated;
        }

        if (
          field === "value" &&
          (qSlug === "date" || qSlug === "dateTime")
        ) {
          updated.value = formatConditionDateValue(value);
          return updated;
        }

        updated[field] = value;
        return updated;
      }),
    );
  };

  const handleDeleteCondition = (id: number) => {
    setDeleteConditionId(id);
    setIsConditionsDelete(true);
  };

  const handleDelete = () => {
    deleteCondition(deleteConditionId, {
      onSuccess: (res) => {
        notify("section conditions deleted successfully!", {
          type: "success",
          position: "bottom-left",
        });
        queryClient.invalidateQueries(["conditionsList"]);
        //navigate(`/settings/forms/${res.data._form.id}/summary`);
      },
      onError: (err) => {
        showError(
          err,
          "A problem occurred while creating the section condition.",
        );
      },
    });
    setIsConditionsDelete(false);
  };

  function isBlankConditionType(typeId: number | string | null | undefined) {
    const conditionType = conditionTypeList?.data?.find(
      (type: any) => type.id === typeId,
    );

    return (
      conditionType?.name === "is_blank" ||
      conditionType?.name === "is_not_blank" ||
      typeId === 10 ||
      typeId === 11
    );
  }

  const getConditionPayloadValue = (condition: any) =>
    isBlankConditionType(condition?._type) ? null : condition.value;

  const getConditionFieldId = (value: any) =>
    value && typeof value === "object" ? value.id : value;

  const getConditionPayloadKey = (condition: any) =>
    [
      getConditionFieldId(condition.section),
      getConditionFieldId(condition.question),
      getConditionFieldId(condition._type),
      getConditionFieldId(condition.value_type),
      condition.value ?? null,
    ].join("|");

  const handleSubmit = async (formikValues: any) => {
    const sectionId = sections[currentIndex]?.id;

    const existingIds = new Set(
      conditionsList?.results?.map((c: any) => c.id) ?? [],
    );
    const existingConditionKeys = new Set(
      conditionsList?.results?.map((condition: any) =>
        getConditionPayloadKey({
          section: condition.section,
          question: condition.question,
          _type: condition._type,
          value_type: condition.value_type,
          value: getConditionPayloadValue(condition),
        }),
      ) ?? [],
    );
    const newConditionKeys = new Set<string>();

    const newConditions = [];
    const updatedConditions = [];
    for (const cond of conditions) {
      // NEW CONDITION — no ID → create
      if (!existingIds.has(cond.id)) {
        const payload = {
          section: sectionId,
          question: cond.question,
          _type: cond._type,
          value_type: cond.value_type,
          value: getConditionPayloadValue(cond),
        };
        const payloadKey = getConditionPayloadKey(payload);

        if (
          !existingConditionKeys.has(payloadKey) &&
          !newConditionKeys.has(payloadKey)
        ) {
          newConditions.push(payload);
          newConditionKeys.add(payloadKey);
        }
      } else {
        // Find original in API list
        const original = conditionsList.results.find(
          (c: any) => c.id === cond.id,
        );

        // Detect changes
        const changed =
          original.question !== cond.question ||
          original._type.id !== cond._type ||
          original.value_type.id !== cond.value_type ||
          original.value !== getConditionPayloadValue(cond);

        if (changed) {
          updatedConditions.push({
            id: cond.id,
            section: sectionId,
            question: cond.question,
            _type: cond._type,
            value_type: cond.value_type,
            value: getConditionPayloadValue(cond),
          });
        }
      }
    }

    // 1. update changed existing conditions
    for (const u of updatedConditions) {
      await updateFormConditions(
        {
          params: {
            section: u.section,
            question: u.question,
            _type: u._type,
            value_type: u.value_type,
            value: u.value,
          },
          id: u.id,
        },
        {
          onSuccess: (res) => {
            notify("section conditions updated successfully!", {
              type: "success",
              position: "bottom-left",
            });
            queryClient.invalidateQueries(["conditionsList", sectionId]);
          },
          onError: (err: any) => {
            //setDoubleClick(false);
            showError(err, "A problem occurred while creating the individual.");
          },
        },
      );
    }

    // 2. create newly added conditions
    if (newConditions.length === 1) {
      await createCondition(newConditions[0], {
        onSuccess: (res) => {
          notify("section conditions created successfully!", {
            type: "success",
            position: "bottom-left",
          });
          queryClient.invalidateQueries(["conditionsList", sectionId]);
          //navigate(`/settings/forms/${res.data._form.id}/summary`);
        },
        onError: (err) => {
          showError(
            err,
            "A problem occurred while creating the section condition.",
          );
        },
      });
    } else if (newConditions.length > 1) {
      await createBulkCondition(newConditions, {
        onSuccess: (res) => {
          notify("bulk conditions created successfully!", {
            type: "success",
            position: "bottom-left",
          });
          queryClient.invalidateQueries(["conditionsList", sectionId]);
          //navigate(`/settings/forms/${res.data._form.id}/summary`);
        },
        onError: (err) => {
          showError(
            err,
            "A problem occurred while creating the bulk section conditions.",
          );
        },
      });
    }
    onRequestClose();
  };

  const mapData: Record<string, string> = {
    short_text: "string",
    long_text: "string",
    number: "integer",
    float: "float",
    integer: "integer",
    date: "date",
    dateTime: "datetime",

    multi_select: "option",
    choice: "option",
    country: "option",

    email: "string",
    link: "string",
    phone_number: "string",
    address: "string",

    file_upload: "null",

    currency: "float",
  };

  // Build the final object EXACTLY like your screenshot
  const buildValueTypeLogic = (conditionValueTypeList: any[]) => {
    if (!Array.isArray(conditionValueTypeList)) return {};

    const backendLookup: Record<string, number> = {};

    conditionValueTypeList.forEach((t) => {
      backendLookup[t.name] = t.id;
    });

    const result: Record<string, number> = {};

    Object.entries(mapData).forEach(([uiKey, value]) => {
      result[uiKey] = backendLookup[value];
    });

    return result;
  };
  const valueTypeLogic = buildValueTypeLogic(conditionValueTypeList?.data);

  const buildAllowedConditionByType = (conditionTypeList) => {
    if (!conditionTypeList || !Array.isArray(conditionTypeList)) {
      return {}; // prevents crash
    }
    const mapConditionTypeList = conditionTypeList.map((c) => c.name);

    const baseOps = {
      string: ["is_blank", "is_not_blank"],
      integer: ["==", "!=", ">", "<", ">=", "<=", "is_blank", "is_not_blank"],
      float: ["==", "!=", ">", "<", ">=", "<=", "is_blank", "is_not_blank"],
      date: ["==", "!=", ">", "<", ">=", "<=", "is_blank", "is_not_blank"],
      datetime: ["==", "!=", ">", "<", ">=", "<=", "is_blank", "is_not_blank"],
      bool: ["==", "!=", "is_blank", "is_not_blank"],
      option: [
        "==",
        "!=",
        "contains",
        "not contains",
        "is_blank",
        "is_not_blank",
      ],
      null: ["is_blank", "is_not_blank"],
    };
    const conditionOverrides = {
      phone_number: ["is_blank", "is_not_blank"],
      address: ["contains", "not contains", "is_blank", "is_not_blank"],
      currency: [">", "<", ">=", "<=", "is_blank", "is_not_blank"],
    };

    return Object.fromEntries(
      Object.entries(mapData).map(([name, value]) => {
        const ops = conditionOverrides[name] ?? baseOps[value] ?? [];
        return [name, ops.filter((op) => mapConditionTypeList.includes(op))];
      }),
    );
  };

  const allowedConditionByType = buildAllowedConditionByType(
    conditionTypeList?.data,
  );

  const isCurrencyPairValid = (cond) => {
    let code: any;
    let amount: any;

    if (typeof cond?.value === "string" && cond.value.includes("|")) {
      code = cond.value.split("|")[0] || "";
      amount = cond.value.split("|")[1] || "";
    } else {
      code = cond.currencyCode ?? null;
      amount = cond.currencyAmount ?? null;
    }

    if (!code && !amount) return false;
    if (code && amount) return true;
    return false;
  };

  const isPhoneNumberPairValid = (cond) => {
    let phoneNumberDialCode: any;
    let phoneNumber: any;

    if (typeof cond?.value === "string" && cond.value.includes("|")) {
      phoneNumberDialCode = cond.value.split("|")[0] || "";
      phoneNumber = cond.value.split("|")[1] || "";
    } else {
      phoneNumberDialCode = cond?.phoneNumberCountryCode ?? null;
      phoneNumber = cond?.phoneNumberValue ?? null;
    }

    if (!phoneNumberDialCode && !phoneNumber) return false;
    if (phoneNumberDialCode && phoneNumber) return true;
    return false;
  };

  const isValidNumberValue = (value: any, qSlug: string) => {
    if (value === "" || value === null || value === undefined) return false;

    if (qSlug === "integer" || qSlug === "number") {
      return /^-?\d+$/.test(String(value));
    }

    if (qSlug === "float") {
      return /^-?\d+(\.\d+)?$/.test(String(value));
    }

    return true;
  };

  const isConditionValid = (cond) => {
    const qInfo = previousQuestions.find((q) => q.id === cond.question);
    const qSlug = qInfo?.field_type?.slug_name || "";

    if (isBlankConditionType(cond?._type)) {
      return Boolean(cond.question && cond._type);
    }

    if (qSlug === "currency") {
      return Boolean(cond.question && cond._type && isCurrencyPairValid(cond));
    }

    if (qSlug === "phone_number") {
      return Boolean(
        cond.question && cond._type && isPhoneNumberPairValid(cond),
      );
    }

    if (qSlug === "integer" || qSlug === "number" || qSlug === "float") {
      return Boolean(
        cond.question &&
          cond._type &&
          cond.value &&
          isValidNumberValue(cond.value, qSlug),
      );
    }

    return Boolean(cond.question && cond._type && cond.value);
  };

  //const canSave = conditions.every(isConditionValid);

  const inputProps = {
    minWidth: 200,
    flex: 1,
  };

  const canAddCondition =
    conditions.length === 0 ||
    isConditionValid(conditions[conditions.length - 1]);

  return (
    <Slider width="700px" isOpen={isOpen} onRequestClose={onRequestClose}>
      <Box px={4} py={3} overflowY="auto" height={"calc(100vh - 211px)"}>
        <Formik
          initialValues={{
            conditionOperator: sections[currentIndex]?.operator,
          }}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {(props) => (
            <Box>
              <H2>Section Conditions</H2>
              <Form onSubmit={props.handleSubmit}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Paragraph>
                    Section {currentIndex + 1} will only show when these
                    conditions are met.
                  </Paragraph>
                </Flex>
                <Flex>
                  <div style={{ width: "48%" }}>
                    <Select
                      flex={1}
                      onChange={(option: any) => {
                        updateSectionOperator(currentIndex, option?.value);
                        props.setFieldValue("conditionOperator", option?.value);
                      }}
                      value={ConditionLabelOptions?.find(
                        ({ value }) => value === props.values.conditionOperator,
                      )}
                      options={ConditionLabelOptions}
                      mt="2"
                      name="conditionOperator"
                      isRequired
                    />
                  </div>
                </Flex>
                <Flex justifyContent={"space-between"} mt={2}>
                  <H3>Conditions</H3>
                </Flex>
                {conditions.length === 0 && (
                  <Paragraph>No conditions added yet</Paragraph>
                )}

                {conditions.map((cond, index) => {
                  // Get actual question info
                  const currentQuestion = previousQuestions.find(
                    (q) => q.id === cond.question,
                  );
                  const qSlug = currentQuestion?.field_type?.slug_name || "";
                  let currency;
                  let amount;
                  let countryCode;
                  let phoneNumber;
                  if (qSlug === "currency") {
                    currency =
                      typeof cond?.value === "string" &&
                      cond.value.includes("|")
                        ? Number(cond.value.split("|")[0])
                        : cond?.currencyCode || "";

                    amount =
                      typeof cond?.value === "string" &&
                      cond.value.includes("|")
                        ? cond.value.split("|")[1]
                        : cond?.currencyAmount || "";
                  }
                  if (qSlug === "phone_number") {
                    countryCode = cond?.value
                      ? Number(cond.value.split("|")[0])
                      : cond?.phoneNumberCountryCode || "";

                    phoneNumber = cond?.value
                      ? cond.value.split("|")[1]
                      : cond?.phoneNumberValue || "";
                  }

                  const allowedConditionOptions =
                    conditionTypeList?.data
                      .filter((c) =>
                        allowedConditionByType[qSlug]?.includes(c.name),
                      )
                      .map((c) => ({
                        value: c.id,
                        label: c.label,
                        name: c.name,
                      })) ?? [];

                  return (
                    <GrayCard key={index} m={0}>
                      <Flex justifyContent="space-between">
                        <H4>Condition {index + 1}</H4>

                        {cond?.id && (
                          <Button
                            variant="danger"
                            Icon={FaTrash}
                            iconSize="12px"
                            onClick={() => handleDeleteCondition(cond.id)}
                            iconColor="white"
                          >
                            Delete
                          </Button>
                        )}
                      </Flex>
                      <Paragraph>If the following question</Paragraph>

                      {/* QUESTION SELECT */}
                      <Select
                        value={
                          previousQuestionOptions.find(
                            (o) => o.value === cond.question,
                          ) || null
                        }
                        options={previousQuestionOptions}
                        onChange={(e: any) => {
                          const q = previousQuestions.find(
                            (item) => item.id === e?.value,
                          );
                          const slug = q?.field_type?.slug_name;
                          const clearedKey = getConditionDateKey(cond, index);

                          setConditions((prev) =>
                            prev.map((con, i) =>
                              i !== index
                                ? con
                                : {
                                    ...con,
                                    question: e?.value,
                                    _type: "",
                                    value: "",
                                    value_type: valueTypeLogic[slug] ?? "",
                                    currencyCode: "",
                                    currencyAmount: "",
                                    phoneNumberCountryCode: "",
                                    phoneNumberValue: "",
                                  },
                            ),
                          );
                          setConditionDateDrafts((previous) => ({
                            ...previous,
                            [clearedKey]: null,
                          }));
                          setResetConditionDateOnNextKeypress((previous) => ({
                            ...previous,
                            [clearedKey]: false,
                          }));
                        }}
                        mt="2"
                        placeholder="Choose question"
                        isRequired
                      />
                      {/* CONDITION TYPE SELECT */}
                      <Select
                        isDisabled={!cond.question}
                        value={
                          allowedConditionOptions.find(
                            (o) => o.value === cond._type,
                          ) || null
                        }
                        options={allowedConditionOptions}
                        onChange={(e: any) => {
                          updateCondition(index, "_type", e.value);
                          updateCondition(index, "value", "");
                          // updateCondition(index, "value_type", "");
                        }}
                        mt="2"
                        placeholder="Choose condition type"
                        isRequired
                      />

                      {!isBlankConditionType(cond?._type) && (
                        <>
                          {(qSlug === "date" || qSlug === "dateTime") && (
                            <div
                              onKeyDownCapture={(
                                event: KeyboardEvent<HTMLDivElement>,
                              ) => {
                                const conditionDateKey = getConditionDateKey(
                                  cond,
                                  index,
                                );

                                if (
                                  !resetConditionDateOnNextKeypress[
                                    conditionDateKey
                                  ]
                                ) {
                                  return;
                                }

                                if (!/^\d$/.test(event.key)) return;

                                setConditionDateDrafts((previous) => ({
                                  ...previous,
                                  [conditionDateKey]: null,
                                }));
                                setResetConditionDateOnNextKeypress(
                                  (previous) => ({
                                    ...previous,
                                    [conditionDateKey]: false,
                                  }),
                                );
                                updateCondition(index, "value", "");
                              }}
                            >
                              <DatePicker
                                value={
                                  conditionDateDrafts[
                                    getConditionDateKey(cond, index)
                                  ] ?? parseConditionDateValue(cond?.value)
                                }
                                onChange={(date: any) => {
                                  const conditionDateKey = getConditionDateKey(
                                    cond,
                                    index,
                                  );
                                  setConditionDateDrafts((previous) => ({
                                    ...previous,
                                    [conditionDateKey]:
                                      getConditionDatePickerValue(date),
                                  }));
                                  setResetConditionDateOnNextKeypress(
                                    (previous) => ({
                                      ...previous,
                                      [conditionDateKey]: false,
                                    }),
                                  );
                                  updateCondition(index, "value", date);
                                }}
                                mt="2"
                                label="Date"
                                name="dateValue"
                                format="dd/MM/yyyy"
                                maxDate={DateTime.now().toJSDate()}
                                {...inputProps}
                              />
                            </div>
                          )}

                          {(qSlug === "number" ||
                            qSlug === "float" ||
                            qSlug === "integer") && (
                            <FormRow my={3}>
                              <Input
                                width={"100%"}
                                value={cond.value || ""}
                                placeholder="Enter number"
                                label="Value"
                                isRequired
                                inputMode="decimal"
                                onChange={(option: any) => {
                                  const rawValue = option.target.value;

                                  // integer only
                                  if (
                                    qSlug === "integer" ||
                                    qSlug === "number"
                                  ) {
                                    const sanitizedValue = rawValue.replace(
                                      /\D/g,
                                      "",
                                    );
                                    updateCondition(
                                      index,
                                      "value",
                                      sanitizedValue,
                                    );
                                    return;
                                  }

                                  // float / decimal
                                  if (qSlug === "float") {
                                    const sanitizedValue = rawValue
                                      .replace(/[^0-9.]/g, "")
                                      .replace(/(\..*)\./g, "$1"); // allow only one dot

                                    updateCondition(
                                      index,
                                      "value",
                                      sanitizedValue,
                                    );
                                  }
                                }}
                              />
                            </FormRow>
                          )}

                          {qSlug === "currency" && (
                            <Flex>
                              <Select
                                width={"30%"}
                                onChange={(option: any) =>
                                  updateCondition(
                                    index,
                                    "currencyCode",
                                    option.value,
                                  )
                                }
                                value={currencyValue?.find(
                                  (value) => value.value === currency,
                                )}
                                options={currencyValue}
                                mt="2"
                                placeholder="Choose currency code"
                              />

                              <Input
                                width={"70%"}
                                value={amount}
                                mt="2"
                                placeholder="Enter amount"
                                onChange={(option: any) =>
                                  updateCondition(
                                    index,
                                    "currencyAmount",
                                    option.target.value,
                                  )
                                }
                              />
                            </Flex>
                          )}

                          {qSlug === "phone_number" && (
                            <Flex>
                              <Select
                                width={"30%"}
                                onChange={(option: any) =>
                                  updateCondition(
                                    index,
                                    "phoneNumberCountryCode",
                                    option.value,
                                  )
                                }
                                value={phoneNumberCountryList?.find(
                                  (value) => value.value === countryCode,
                                )}
                                options={phoneNumberCountryList}
                                mt="2"
                                placeholder="Choose country code"
                              />

                              <Input
                                width={"70%"}
                                value={phoneNumber || ""}
                                mt="2"
                                placeholder="Enter phone number"
                                onChange={(option: any) =>
                                  updateCondition(
                                    index,
                                    "phoneNumberValue",
                                    option.target.value,
                                  )
                                }
                              />
                            </Flex>
                          )}

                          {(qSlug === "country" || qSlug === "address") && (
                            <Select
                              flex={1}
                              onChange={(option: any) =>
                                updateCondition(index, "value", option.value)
                              }
                              value={
                                cond?.value
                                  ? countryList?.find(
                                      ({ value }) =>
                                        value === Number(cond.value),
                                    )
                                  : null
                              }
                              options={countryList}
                              mt="2"
                              placeholder="Choose value"
                            />
                          )}

                          {(qSlug === "choice" || qSlug === "multi_select") && (
                            <Input
                              width={"100%"}
                              value={cond.value || ""}
                              placeholder="Enter value"
                              label="Value"
                              isRequired
                              onChange={(option: any) =>
                                updateCondition(
                                  index,
                                  "value",
                                  option.target.value,
                                )
                              }
                            />
                          )}

                          {![
                            "date",
                            "dateTime",
                            "number",
                            "float",
                            "integer",
                            "currency",
                            "phone_number",
                            "country",
                            "address",
                            "choice",
                            "multi_select",
                          ].includes(qSlug) && (
                            <FormRow my={3}>
                              <Input
                                width={"100%"}
                                value={cond.value || ""}
                                placeholder="Enter value"
                                label="Value"
                                isRequired
                                onChange={(option: any) =>
                                  updateCondition(
                                    index,
                                    "value",
                                    option.target.value,
                                  )
                                }
                              />
                            </FormRow>
                          )}
                        </>
                      )}
                    </GrayCard>
                  );
                })}
                <Flex justifyContent={"flex-end"}>
                  <Button
                    Icon={FaPlus}
                    iconSize="16px"
                    iconColor="blue.primary"
                    variant="blueText"
                    onClick={addCondition}
                    fontWeight={"bold"}
                    isDisabled={!canAddCondition}
                  >
                    Add condition
                  </Button>
                </Flex>

                <Flex gap="12" justifyContent={"end"} mt={5}>
                  <Button
                    variant="primary"
                    onClick={() => handleSubmit(props)}
                    isDisabled={!canAddCondition}
                  >
                    Save
                  </Button>
                  <Button variant="secondary" onClick={() => onRequestClose()}>
                    Cancel
                  </Button>
                </Flex>
              </Form>

              <Modal
                isOpen={isConditionsDelete}
                onClose={() => setIsConditionsDelete(false)}
                title="Delete conditions?"
                content="This condition will be permanently deleted."
                onModalConfirmClick={() => handleDelete()}
                secondaryButtonText="Cancel"
                primaryButtonText="Delete"
                primaryButtonVariant="danger"
                hasSecondaryButton
                width="450px"
              />
            </Box>
          )}
        </Formik>
      </Box>
    </Slider>
  );
};
